import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarTrigger,
  SidebarInset,
  SidebarFooter,
  useSidebar,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  CollapsibleSidebarMenuSub
} from '@/components/ui/sidebar';

import { AlertCircle, LogOut } from 'lucide-react';
import { Home, User, Calendar, FileText, CreditCard, Settings, Sparkles, BarChart3, CalendarDays, Heart, MessageCircle, ChevronDown, Users, Bookmark, PenSquare, MessagesSquare, Compass } from 'lucide-react';
import { useCommunityProfile } from '@/hooks/useCommunityProfile';

import ClientProfile from '@/components/dashboard/client/ClientProfile';
import UpcomingAppointments from '@/components/dashboard/client/UpcomingAppointments';
import AppointmentHistory from '@/components/dashboard/client/AppointmentHistory';
import ClientAppointmentsView from '@/components/dashboard/client/ClientAppointmentsView';
import ClientUpcomingGroupSessions from '@/components/dashboard/client/ClientUpcomingGroupSessions';

// Lazy load heavy components for better performance
const PaymentPage = lazy(() => import('@/components/dashboard/client/PaymentPage'));
const AINavigatorChat = lazy(() => import('@/components/dashboard/client/ai-navigator/AINavigatorChat'));
const ClientSettings = lazy(() => import('@/components/dashboard/client/ClientSettings'));
const TrackingAnalysis = lazy(() => import('@/components/dashboard/client/tracking/TrackingAnalysis'));
const CalendarView = lazy(() => import('@/components/calendar/CalendarView'));
const SavedPractitioners = lazy(() => import('@/components/dashboard/client/SavedPractitioners'));
import Navbar from '@/components/Navbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarMenuButtonWithAutoHide } from '@/components/dashboard/shared/SidebarMenuButtonWithAutoHide';
import { UnifiedLoader } from '@/components/dashboard/shared/UnifiedLoader';
import { UnifiedError } from '@/components/dashboard/shared/UnifiedError';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUnreadSystemMessages } from '@/hooks/useUnreadSystemMessages';
import SystemMessagesList from '@/components/dashboard/shared/SystemMessagesList';
import { getFullName } from '@/utils/name-helpers';
import { TimezoneDetectionAlert } from '@/components/dashboard/shared/TimezoneDetectionAlert';
import OnboardingTour from '@/components/onboarding/OnboardingTour';
import { isTimezoneConfigured } from '@/utils/timezone-requirement';
import TimezoneConfirmationBanner from '@/components/timezone/TimezoneConfirmationBanner';

const ClientDashboard = () => {
  const { user, isLoading: authLoading, activeRole, signOut, roleReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const isMobile = useIsMobile();

  // Unread messages hook
  const { unreadCounts, refreshUnreadCounts } = useUnreadMessages({
    userId: undefined, // Will be set by the hook from auth context
    userType: 'client'
  });

  // Unread system messages hook
  const {
    unreadCount: systemMessagesUnreadCount,
    markAsViewed,
    refreshUnreadCount: refreshSystemMessagesUnreadCount,
  } = useUnreadSystemMessages('client');

  // Unread counts reported by child components (avoids duplicate Supabase queries)
  const [upcomingUnreadCount, setUpcomingUnreadCount] = useState(0);
  const [historyUnreadCount, setHistoryUnreadCount] = useState(0);
  
  // Parse query parameters to get initial tab
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'home';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      navigate('/');
      notify.success("Logged Out", "You've been successfully logged out");
    } catch (error) {
      console.error('Sign out error:', error);
      notify.error("Error", "Failed to sign out. Please try again.");
    }
  }, [signOut, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      if (authLoading || !roleReady) return;

      setError(null);
      
      try {
        if (!user) {
          notify.error("Unauthorized", "Please login to view your dashboard");
          navigate("/client-auth");
          setIsLoading(false);
          return;
        }

        let data: any = null;
        let fetchError: any = null;

        // First attempt
        const result1 = await supabase
          .from('clients')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        
        if (result1.error) {
          console.warn("First client profile fetch failed, retrying in 1s...", result1.error);
          // Retry once after 1s delay (handles transient auth session timing)
          await new Promise(resolve => setTimeout(resolve, 1000));
          const result2 = await supabase
            .from('clients')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();
          data = result2.data;
          fetchError = result2.error;
        } else {
          data = result1.data;
          fetchError = result1.error;
        }
        
        if (fetchError) {
          console.error("Error fetching client profile:", fetchError);
          setError("Could not load your client profile");
          setIsLoading(false);
          return;
        }

        if (!data) {
          console.log("No client profile found. Checking if user is a practitioner...");
          // Auto-detect correct role and redirect
          const { data: practData } = await supabase
            .from('practitioners')
            .select('id')
            .eq('id', user.id)
            .maybeSingle();
          if (practData) {
            console.log("User is a practitioner, signing out and redirecting to practitioner auth...");
            notify.error("Wrong Portal", "This account is registered as a practitioner. Please use the practitioner login page.");
            await signOut();
            navigate('/practitioner-auth', { replace: true });
            return;
          }
          setProfileMissing(true);
          setIsLoading(false);
          return;
        }
        
        setClientProfile(data);
        setIsLoading(false);
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred while loading your profile");
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [user, authLoading, roleReady, navigate]);

  // Update URL when tab changes (only depends on activeTab to avoid circular navigate/location.search loop)
  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', activeTab);
    const newSearch = newParams.toString();
    // Only navigate if the tab param actually changed to avoid unnecessary renders
    if (location.search !== `?${newSearch}`) {
      navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Mark system messages as viewed when user visits home tab
  useEffect(() => {
    if (activeTab === 'home') {
      markAsViewed();
    }
  }, [activeTab, markAsViewed]);

  const timezoneConfigured = isTimezoneConfigured(clientProfile?.timezone);

  const handleGoToTimezoneSettings = useCallback(() => {
    setActiveTab('profile');
    setTimeout(() => {
      const timezoneField = document.getElementById('client-timezone-field');
      timezoneField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldFocusTimezone = params.get('focus') === 'timezone';
    if (activeTab !== 'profile' || !shouldFocusTimezone) return;

    const timer = setTimeout(() => {
      const timezoneField = document.getElementById('client-timezone-field');
      timezoneField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);

    return () => clearTimeout(timer);
  }, [activeTab, location.search]);

  if (authLoading || !roleReady || isLoading) {
    return <UnifiedLoader message={t('loading')} fullScreen />;
  }

  if (error) {
    return (
      <UnifiedError 
        message={error} 
        onRetry={() => window.location.reload()} 
        fullScreen
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      {/* Timezone detection - runs once per session */}
      {user && clientProfile && (
        <TimezoneDetectionAlert
          userId={user.id}
          userType="client"
          currentTimezone={clientProfile.timezone ?? null}
        />
      )}
      <SidebarProvider>
        <div className="min-h-screen flex w-full pt-16">
          <Sidebar variant="sidebar" collapsible="icon" className="border-r border-gray-100 transition-all duration-300 ease-in-out">
            <SidebarHeader className="border-b px-4 py-3">
              <div className="text-lg font-bold text-amber-600">{t('dashboard')}</div>
            </SidebarHeader>
            <ClientSidebarMenu 
              activeTab={activeTab} 
              setActiveTab={setActiveTab}
              systemMessagesUnreadCount={systemMessagesUnreadCount}
              upcomingUnreadCount={upcomingUnreadCount}
              historyUnreadCount={historyUnreadCount}
              navigate={navigate}
            />

            <ClientFooter onSignOut={handleSignOut} userEmail={user?.email} />
          </Sidebar>
          <SidebarInset className="bg-gray-50 flex-1 transition-all duration-300 ease-in-out">
            <header className="sticky top-0 z-40 bg-white border-b h-16 flex items-center px-4">
              <SidebarTrigger className="mr-4" />
            </header>
              <div className="w-full p-2 sm:p-6">
                <div className="bg-white rounded-lg p-3 sm:p-6 shadow-sm w-full">
                  <TimezoneConfirmationBanner />
                  {profileMissing && (
                    <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">
                      <p className="font-medium">Client profile not found.</p>
                      <p className="text-sm mt-1">Please complete your profile in Settings to unlock all features.</p>
                    </div>
                  )}
                  {
                  activeTab === 'home' && (
                    <div className="min-h-[400px] space-y-6">
                      <div>
                        <h2 className="text-2xl font-medium mb-4">{t('welcomeToDashboard')}</h2>
                        <p className="text-gray-600 mb-6">
                          {t('hello')} {getFullName(clientProfile) || user?.email?.split('@')[0] || 'there'}! {t('personalDashboard')}
                        </p>
                        <p className="text-gray-600 mb-6">
                          {t('navigateToManage')}
                        </p>
                      </div>
                      
                      <SystemMessagesList 
                        userType="client" 
                        onUnreadCountChange={refreshSystemMessagesUnreadCount}
                      />
                      
                      <ClientUpcomingGroupSessions />
                    </div>
                  )
                  }
                  
                  {activeTab === 'profile' && (
                    <ClientProfile profile={clientProfile} setProfile={setClientProfile} />
                  )}
                  
                  {activeTab === 'appointments' && (
                    <ClientAppointmentsView
                      unreadByBooking={unreadCounts.unreadByBooking}
                      onRefreshUnreadCounts={refreshUnreadCounts}
                      onUpcomingUnreadCountChange={setUpcomingUnreadCount}
                      onHistoryUnreadCountChange={setHistoryUnreadCount}
                    />
                  )}
                  
                  {activeTab === 'ai-navigator' && (
                    <Suspense fallback={<UnifiedLoader message="Loading AI Navigator..." />}>
                      <AINavigatorChat />
                    </Suspense>
                  )}

                  {activeTab === 'payment' && (
                    <Suspense fallback={<UnifiedLoader message="Loading payment..." />}>
                      <PaymentPage />
                    </Suspense>
                  )}
                  
                  {activeTab === 'tracking' && (
                    <Suspense fallback={<UnifiedLoader message="Loading tracking analysis..." />}>
                      <TrackingAnalysis />
                    </Suspense>
                  )}
                  
                  {activeTab === 'calendar' && (
                    <Suspense fallback={<UnifiedLoader message="Loading calendar..." />}>
                      <CalendarView />
                    </Suspense>
                  )}
                  
                  {activeTab === 'settings' && (
                    <Suspense fallback={<UnifiedLoader message="Loading settings..." />}>
                      <ClientSettings clientId={user?.id} profile={clientProfile} setProfile={setClientProfile} />
                    </Suspense>
                  )}

                  {activeTab === 'saved-practitioners' && (
                    <Suspense fallback={<UnifiedLoader message="Loading saved practitioners..." />}>
                      <SavedPractitioners />
                    </Suspense>
                  )}
                </div>
              </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
      
      {/* Onboarding Tour */}
      <OnboardingTour userType="client" />
    </div>
  );
};

// Client sidebar menu with grouped accordion structure
function ClientSidebarMenu({ 
  activeTab, 
  setActiveTab,
  systemMessagesUnreadCount,
  upcomingUnreadCount,
  historyUnreadCount,
  navigate
}: { 
  activeTab: string; 
  setActiveTab: (tab: string) => void;
  systemMessagesUnreadCount: number;
  upcomingUnreadCount: number;
  historyUnreadCount: number;
  navigate: (path: string) => void;
}) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const { expanded, toggleExpanded } = useSidebar();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Bookings']);
  const { data: communityProfile } = useCommunityProfile();

  // Listen for tour-expand-group events
  useEffect(() => {
    const handler = (e: Event) => {
      const group = (e as CustomEvent).detail?.group;
      if (group) {
        setExpandedGroups([group]);
      }
    };
    window.addEventListener('tour-expand-group', handler);
    return () => window.removeEventListener('tour-expand-group', handler);
  }, []);

  // Auto-expand group based on active tab (accordion: only one open)
  useEffect(() => {
    if (['calendar', 'appointments', 'saved-practitioners'].includes(activeTab)) {
      setExpandedGroups(['Bookings']);
    } else if (['ai-navigator', 'tracking'].includes(activeTab)) {
      setExpandedGroups(['Wellness']);
    } else if (['profile', 'payment'].includes(activeTab)) {
      setExpandedGroups(['Account']);
    }
  }, [activeTab]);

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => prev.includes(group) ? [] : [group]);
  };

  const handleSubClick = (tab: string) => {
    setActiveTab(tab);
    if (isMobile && expanded) {
      setTimeout(() => toggleExpanded(), 100);
    }
  };

  const handleNavigateExternal = (path: string) => {
    navigate(path);
    if (isMobile && expanded) {
      setTimeout(() => toggleExpanded(), 100);
    }
  };

  const totalAppointmentUnread = upcomingUnreadCount + historyUnreadCount;


  return (
    <SidebarContent>
      <SidebarMenu>
        {/* Home — standalone */}
        <SidebarMenuItem data-tour="client-home">
          <SidebarMenuButtonWithAutoHide
            tooltip={t('home')}
            onClick={() => setActiveTab('home')}
            isActive={activeTab === 'home'}
            showBadge={true}
            unreadCount={systemMessagesUnreadCount}
            countRefreshTrigger={systemMessagesUnreadCount}
          >
            <Home className="mr-2 h-5 w-5" />
            <span>{t('home')}</span>
          </SidebarMenuButtonWithAutoHide>
        </SidebarMenuItem>

        {/* Bookings group — user's appointments & sessions */}
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="My Appointments"
            onClick={() => toggleGroup('Bookings')}
            isActive={['calendar', 'appointments', 'saved-practitioners'].includes(activeTab)}
            className="w-full justify-between"
          >
            <div className="flex items-center">
              <Calendar className="mr-2 h-5 w-5" />
              <span>My Appointments</span>
            </div>
            <div className="flex items-center">
              {(!expandedGroups.includes('Bookings') || (!expanded && !isMobile)) && totalAppointmentUnread > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center mr-2">
                  {totalAppointmentUnread > 99 ? '99+' : totalAppointmentUnread}
                </span>
              )}
              {(expanded || isMobile) && (
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedGroups.includes('Bookings') ? "rotate-180" : ""}`} />
              )}
            </div>
          </SidebarMenuButton>
          <CollapsibleSidebarMenuSub open={expandedGroups.includes('Bookings') && (expanded || isMobile)}>
              <SidebarMenuSubItem data-tour="client-appointments">
                <SidebarMenuSubButton onClick={() => handleSubClick('appointments')} isActive={activeTab === 'appointments'}>
                  <Calendar className="mr-2 h-5 w-5" />
                  <span>Upcoming & Past</span>
                  {totalAppointmentUnread > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                      {totalAppointmentUnread > 99 ? '99+' : totalAppointmentUnread}
                    </span>
                  )}
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem data-tour="client-calendar">
                <SidebarMenuSubButton onClick={() => handleSubClick('calendar')} isActive={activeTab === 'calendar'}>
                  <CalendarDays className="mr-2 h-5 w-5" />
                  <span>{t('calendar.calendar')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem data-tour="client-saved-practitioners">
                <SidebarMenuSubButton onClick={() => handleSubClick('saved-practitioners')} isActive={activeTab === 'saved-practitioners'}>
                  <Heart className="mr-2 h-5 w-5" />
                  <span>Saved Practitioners</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
          </CollapsibleSidebarMenuSub>
        </SidebarMenuItem>

        {/* Community Activity group — what the user posts, replies to, and bookmarks */}
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Community"
            onClick={() => toggleGroup('Community')}
            className="w-full justify-between"
          >
            <div className="flex items-center">
              <MessagesSquare className="mr-2 h-5 w-5" />
              <span>Community</span>
            </div>
            {(expanded || isMobile) && (
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedGroups.includes('Community') ? "rotate-180" : ""}`} />
            )}
          </SidebarMenuButton>
          <CollapsibleSidebarMenuSub open={expandedGroups.includes('Community') && (expanded || isMobile)}>
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  onClick={() => communityProfile?.handle
                    ? handleNavigateExternal(`/community/u/${communityProfile.handle}`)
                    : handleNavigateExternal('/community')}
                >
                  <PenSquare className="mr-2 h-5 w-5" />
                  <span>My Posts</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  onClick={() => communityProfile?.handle
                    ? handleNavigateExternal(`/community/u/${communityProfile.handle}?tab=replies`)
                    : handleNavigateExternal('/community')}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  <span>Replies</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem>
                <SidebarMenuSubButton onClick={() => handleNavigateExternal('/community/saved')}>
                  <Bookmark className="mr-2 h-5 w-5" />
                  <span>Bookmarks</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
          </CollapsibleSidebarMenuSub>
        </SidebarMenuItem>

        {/* Wellness group */}
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Wellness"
            onClick={() => toggleGroup('Wellness')}
            isActive={['ai-navigator', 'tracking'].includes(activeTab)}
            className="w-full justify-between"
          >

            <div className="flex items-center">
              <Sparkles className="mr-2 h-5 w-5" />
              <span>Wellness</span>
            </div>
            {(expanded || isMobile) && (
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedGroups.includes('Wellness') ? "rotate-180" : ""}`} />
            )}
          </SidebarMenuButton>
          <CollapsibleSidebarMenuSub open={expandedGroups.includes('Wellness') && (expanded || isMobile)}>
              <SidebarMenuSubItem data-tour="client-ai-navigator">
                <SidebarMenuSubButton onClick={() => handleSubClick('ai-navigator')} isActive={activeTab === 'ai-navigator'}>
                  <Compass className="mr-2 h-5 w-5" />
                  <span>{t('aiNavigator')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem data-tour="client-tracking">
                <SidebarMenuSubButton onClick={() => handleSubClick('tracking')} isActive={activeTab === 'tracking'}>
                  <BarChart3 className="mr-2 h-5 w-5" />
                  <span>{t('tracking')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
          </CollapsibleSidebarMenuSub>
        </SidebarMenuItem>

        {/* Account group */}
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Account"
            onClick={() => toggleGroup('Account')}
            isActive={['profile', 'payment'].includes(activeTab)}
            className="w-full justify-between"
          >
            <div className="flex items-center">
              <User className="mr-2 h-5 w-5" />
              <span>Account</span>
            </div>
            {(expanded || isMobile) && (
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedGroups.includes('Account') ? "rotate-180" : ""}`} />
            )}
          </SidebarMenuButton>
          <CollapsibleSidebarMenuSub open={expandedGroups.includes('Account') && (expanded || isMobile)}>
              <SidebarMenuSubItem data-tour="client-profile">
                <SidebarMenuSubButton onClick={() => handleSubClick('profile')} isActive={activeTab === 'profile'}>
                  <User className="mr-2 h-5 w-5" />
                  <span>{t('myProfile')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem data-tour="client-payment">
                <SidebarMenuSubButton onClick={() => handleSubClick('payment')} isActive={activeTab === 'payment'}>
                  <CreditCard className="mr-2 h-5 w-5" />
                  <span>{t('paymentMethods')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
          </CollapsibleSidebarMenuSub>
        </SidebarMenuItem>

        {/* Settings — standalone */}
        <SidebarMenuItem data-tour="client-settings">
          <SidebarMenuButtonWithAutoHide
            tooltip={t('settings')}
            onClick={() => setActiveTab('settings')}
            isActive={activeTab === 'settings'}
            countRefreshTrigger={0}
          >
            <Settings className="mr-2 h-5 w-5" />
            <span>{t('settings')}</span>
          </SidebarMenuButtonWithAutoHide>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarContent>
  );
}

// Simplified footer component with stable layout
function ClientFooter({ onSignOut, userEmail }: { onSignOut: () => void; userEmail?: string }) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();
  const { t } = useTranslation('dashboard');

  return (
    <SidebarFooter className="border-t mt-auto p-4">
      <SidebarMenuItem>
        <SidebarMenuButton 
          tooltip={t('logout')} 
          onClick={onSignOut}
          className="text-gray-600 hover:text-red-600 justify-center"
        >
          <LogOut className={expanded || isMobile ? "mr-2" : ""} />
          {(expanded || isMobile) && <span>{t('logout')}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
      {userEmail && (
        <div className={`mt-2 text-xs text-gray-500 px-2 transition-opacity duration-200 ${
          expanded || isMobile ? 'opacity-100' : 'opacity-0'
        }`}>
          {(expanded || isMobile) && (
            <span>{t('loggedInAs')} {userEmail}</span>
          )}
        </div>
      )}
    </SidebarFooter>
  );
}

export default ClientDashboard;
